import { create } from "zustand";
import { applyRestaurantStyles } from "../utils/stylesApplier";
import { fallbackConfig } from "../fallback/fallbackConfig";
import type { RestaurantConfig, Theme } from "../ui/uiConfig";

// 🔥 CORE: navegación principal
type SectionType =
  | "home"
  | "productModal"
  | "cart";

// Guardamos scroll por sección origen
type ScrollOrigin = "home" | "cart" | "search";

interface UIState {
  // --- CONFIG BASE ---
  config: RestaurantConfig;
  theme: Theme;
  lang: string;
  translations: Record<string, Record<string, string>>;

  // --- SEARCH ---
  showSearch: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;

  // --- STICKY ---
  isSticky: boolean;
  setIsSticky: (val: boolean) => void;

  // 🔥 NAV PRINCIPAL
  section: SectionType;
  setSection: (section: SectionType) => void;

  // 🔥 SCROLL POSITIONS — una por sección origen
  scrollPositions: Record<ScrollOrigin, number>;
  saveScrollPosition: (origin: ScrollOrigin) => void;
  restoreScrollPosition: (origin: ScrollOrigin) => void;

  // --- THEME ---
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  // --- LANG ---
  setLang: (lang: string) => void;

  // --- CONFIG ---
  setConfig: (config: RestaurantConfig) => void;
  setTranslations: (translations: Record<string, Record<string, string>>) => void;
  // --- DEMO OVERRIDE ---
  // --- DEMO OVERRIDE ---
  productOverride: {
    id: string;
    name: string;
    price: number;
    description?: string;
  } | null;
  setProductOverride: (override: {
    id: string;
    name: string;
    price: number;
    description?: string;
  } | null) => void;
  demoPhone: string | null;
  demoLogo: string | null;
  demoColor: string | null;
  setDemoConfig: (phone: string | null, logo: string | null, color: string | null) => void;
}

const THEME_STORAGE_KEY = "restaurant-theme";

const getInitialTheme = (): Theme => {
  if (typeof window !== "undefined") {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === "light" || storedTheme === "dark") return storedTheme;
  }
  return fallbackConfig.colors.defaultTheme;
};

export const useUIStore = create<UIState>((set, get) => ({
  // --- BASE ---
  config: fallbackConfig,
  theme: getInitialTheme(),
  lang: fallbackConfig.i18n.defaultLang,
  translations: fallbackConfig.i18n.translations,

  // --- SEARCH ---
  showSearch: false,
  openSearch: () =>
    set({
      showSearch: true,
      section: "home",
    }),
  closeSearch: () => set({ showSearch: false }),
  toggleSearch: () =>
    set((s) => ({ showSearch: !s.showSearch })),

  // --- STICKY ---
  isSticky: false,
  setIsSticky: (val) => set({ isSticky: val }),

  // 🔥 NAV PRINCIPAL
  section: "home",
  setSection: (section) => set({ section }),

  // 🔥 SCROLL POSITIONS
  scrollPositions: { home: 0, cart: 0, search: 0 },

  saveScrollPosition: (origin) => {
    set((s) => ({
      scrollPositions: {
        ...s.scrollPositions,
        [origin]: window.scrollY,
      },
    }));
  },

  restoreScrollPosition: (origin) => {
    const y = get().scrollPositions[origin];
    requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: "instant" });
    });
  },

  // --- THEME ---
  setTheme: (theme: Theme) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
    const config = get().config ?? fallbackConfig;
    applyRestaurantStyles(config, theme);
    set({ theme });
  },

  toggleTheme: () => {
    const current = get().theme;
    get().setTheme(current === "light" ? "dark" : "light");
  },

  // --- LANG ---
  setLang: (lang: string) => set({ lang }),

  // --- CONFIG ---
  setConfig: (config: RestaurantConfig) => {
    const theme = get().theme;
    applyRestaurantStyles(config, theme);
    set({ config });
  },

  setTranslations: (translations) => set({ translations }),
  productOverride: null,
  setProductOverride: (override) => set({ productOverride: override }),
  demoPhone: null,
  demoLogo: null,
  demoColor: null,
  setDemoConfig: (phone, logo, color) => set({ demoPhone: phone, demoLogo: logo, demoColor: color }),
}));
